/**
 * Pure helpers for the comments-list popover: deriving the "All comments"
 * fetch endpoint, labeling which page a thread lives on, and parsing/sorting the
 * server response. All side-effect free and unit-tested; the DOM glue that uses
 * them lives in the embed controller.
 */

import { type AnnotationThread, AnnotationThreadSchema } from "../types";

/**
 * Turn the widget's collaboration `serverUrl` (a ws/wss endpoint) into its
 * http(s) origin+path base. Returns null for anything that is not a
 * ws/wss/http/https URL so callers never build a request against a hostile
 * scheme (js:, data:, …).
 */
export function collabHttpBaseUrl(
  serverUrl: string | undefined,
): string | null {
  if (!serverUrl) return null;
  let url: URL;
  try {
    url = new URL(serverUrl);
  } catch {
    return null;
  }

  const httpProtocol =
    url.protocol === "wss:"
      ? "https:"
      : url.protocol === "ws:"
        ? "http:"
        : url.protocol;
  if (httpProtocol !== "http:" && httpProtocol !== "https:") return null;

  url.protocol = httpProtocol;
  // Drop a trailing slash so `${base}/api/...` never doubles up.
  return url.toString().replace(/\/+$/, "");
}

/**
 * Build the `GET /api/review-threads` endpoint for a review, or null when the
 * server URL is unusable. projectId/reviewId are passed as query params.
 */
export function reviewThreadsEndpoint(
  serverUrl: string | undefined,
  projectId: string,
  reviewId: string,
): string | null {
  const base = collabHttpBaseUrl(serverUrl);
  if (!base) return null;

  const endpoint = new URL(`${base}/api/review-threads`);
  endpoint.searchParams.set("projectId", projectId);
  endpoint.searchParams.set("reviewId", reviewId);
  return endpoint.toString();
}

/**
 * Human label for which page a thread belongs to, relative to the page the
 * widget is currently mounted on:
 * - "this page" when the thread's pageId matches the current page.
 * - the thread target URL's `pathname + hash` otherwise (http(s) only).
 * - the raw pageId (or a generic fallback) when no usable URL is available.
 */
export function commentPageLabel(
  thread: AnnotationThread,
  currentPageId: string | undefined,
): string {
  if (
    thread.pageId !== undefined &&
    currentPageId !== undefined &&
    thread.pageId === currentPageId
  ) {
    return "this page";
  }

  const url = "url" in thread.target ? thread.target.url : undefined;
  if (url) {
    try {
      const parsed = new URL(url);
      if (parsed.protocol === "http:" || parsed.protocol === "https:") {
        return `${parsed.pathname}${parsed.hash}` || "/";
      }
    } catch {
      // fall through to the pageId fallback
    }
  }

  return thread.pageId ?? "other page";
}

/**
 * Validate the `{ threads: [...] }` payload from the review-threads endpoint,
 * keeping only entries that match the AnnotationThread schema. A single
 * malformed thread never discards the rest, and a malformed envelope yields [].
 */
export function parseReviewThreadsResponse(data: unknown): AnnotationThread[] {
  if (!data || typeof data !== "object") return [];
  const threads = (data as { threads?: unknown }).threads;
  if (!Array.isArray(threads)) return [];

  const result: AnnotationThread[] = [];
  for (const entry of threads) {
    const parsed = AnnotationThreadSchema.safeParse(entry);
    if (parsed.success) result.push(parsed.data);
  }
  return result;
}

/** Newest-first by `updatedAt` (unparseable dates sort last). Non-mutating. */
export function sortThreadsByUpdatedDesc(
  threads: AnnotationThread[],
): AnnotationThread[] {
  return [...threads].sort((a, b) => {
    const ta = Date.parse(a.updatedAt) || 0;
    const tb = Date.parse(b.updatedAt) || 0;
    return tb - ta;
  });
}
